<script setup>
import { ref, computed, onMounted } from 'vue'

const status = ref('')   // 'required' | 'pending' | ''
const email = ref('')
const github = ref('')
const dismissed = ref(false)
const submitting = ref(false)
const submitted = ref(false)
const submitError = ref('')

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.krate.sh'

// Logging out of github.com first lets the user re-OAuth as a different
// identity. After logout, GitHub redirects back to app.krate.sh/login.
const switchAccountUrl = 'https://github.com/logout?return_to=https://app.krate.sh/login'

onMounted(() => {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)
  const s = params.get('signup')
  // 'conflict' has its own dedicated page (/auth/conflict) — not handled here.
  if (s !== 'required' && s !== 'pending') return
  status.value = s
  email.value = params.get('email') || ''
  github.value = params.get('github') || ''
})

const visible = computed(() =>
  !dismissed.value && (status.value === 'required' || status.value === 'pending')
)

async function signUp() {
  if (submitting.value || submitted.value) return
  submitting.value = true
  submitError.value = ''
  try {
    const resp = await fetch(`${API_BASE}/api/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.value,
        // Use the GitHub username as the display name fallback so the
        // signup row has *something* recognizable until the user is
        // approved and fills out a real profile.
        name: github.value || undefined,
        source: 'gated-login',
      }),
    })
    if (resp.ok) {
      submitted.value = true
      // Drop a cross-subdomain cookie so app.krate.sh/login can short-circuit
      // the OAuth round-trip and route the user straight back to the pending
      // banner. 24h is enough to dodge spurious re-bounces during the
      // "I just applied" window without holding stale state forever — once
      // the application is approved, real auth (auth-server set-cookie on
      // success path) replaces the session and the next /api/user clears
      // this hint.
      try {
        document.cookie = `krate_application=${encodeURIComponent(email.value)};` +
          ` Domain=.krate.sh; Path=/; Max-Age=86400; SameSite=Lax; Secure`
      } catch {}
      // Strip the ?signup= query so a refresh doesn't re-trigger the banner.
      if (typeof window !== 'undefined' && window.history?.replaceState) {
        window.history.replaceState({}, '', window.location.pathname)
      }
    } else {
      const text = await resp.text().catch(() => '')
      submitError.value = text || `Server returned ${resp.status}.`
    }
  } catch {
    submitError.value = 'Network error. Please try again.'
  } finally {
    submitting.value = false
  }
}

function dismiss() {
  dismissed.value = true
  if (typeof window !== 'undefined' && window.history?.replaceState) {
    window.history.replaceState({}, '', window.location.pathname)
  }
}
</script>

<template>
  <div v-if="visible" class="signup-gate" role="alert">
    <button @click="dismiss" class="btn-dismiss" aria-label="Dismiss">×</button>
    <div class="signup-gate-inner">
      <div class="signup-gate-text">
        <template v-if="submitted">
          <p class="signup-gate-headline">
            Thanks — your application for <strong>{{ email }}</strong> is in.
          </p>
          <p class="signup-gate-sub">
            We'll email you when it's approved. Want to switch accounts in the meantime? Use a different GitHub identity below.
          </p>
        </template>
        <template v-else-if="status === 'required'">
          <p class="signup-gate-headline">
            We don't recognize <strong>{{ email }}</strong>
            <span v-if="github" class="signup-gate-github"> ({{ '@' + github }})</span>
            for the krate beta.
          </p>
          <p class="signup-gate-sub">
            Sign up with this account, or sign in with a different GitHub identity.
          </p>
        </template>
        <template v-else>
          <p class="signup-gate-headline">
            Your application for <strong>{{ email }}</strong> is pending review.
          </p>
          <p class="signup-gate-sub">
            We'll email you when it's approved. To switch accounts, sign in with a different GitHub identity.
          </p>
        </template>
        <p v-if="submitError" class="signup-gate-error">{{ submitError }}</p>
      </div>
      <div class="signup-gate-actions">
        <button
          v-if="status === 'required' && !submitted"
          @click="signUp"
          :disabled="submitting"
          class="btn-primary"
        >
          {{ submitting ? 'Submitting…' : 'Sign up with this account' }}
        </button>
        <a :href="switchAccountUrl" class="btn-secondary">
          Use a different GitHub account
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped>
.signup-gate {
  position: sticky;
  top: 0;
  z-index: 60;
  background: var(--color-krate-blue-600);
  color: white;
  border-bottom: 1px solid var(--color-krate-blue-700);
}
.signup-gate .btn-dismiss {
  position: absolute;
  top: 50%;
  right: 0.6rem;
  transform: translateY(-50%);
  background: transparent;
  color: rgba(255, 255, 255, 0.65);
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.35rem 0.55rem;
  border-radius: var(--radius-md);
}
.signup-gate .btn-dismiss:hover { color: white; background: rgba(255, 255, 255, 0.08); }
.signup-gate-inner {
  max-width: 80rem;
  margin: 0 auto;
  padding: 1rem 2.5rem 1rem 1.25rem;  /* extra right padding to clear the × */
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
@media (min-width: 768px) {
  .signup-gate-inner {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
  }
}
.signup-gate-text { line-height: 1.5; }
.signup-gate-headline { font-size: 0.95rem; font-weight: 500; }
.signup-gate-github { color: var(--color-krate-blue-100); font-weight: 400; }
.signup-gate-sub { font-size: 0.85rem; color: var(--color-krate-blue-100); margin-top: 0.15rem; }
.signup-gate-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.btn-primary {
  background: white;
  color: var(--color-krate-blue-700);
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.5rem 0.9rem;
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  white-space: nowrap;
}
.btn-primary:hover:not(:disabled) { background: var(--color-krate-blue-50); }
.btn-primary:disabled { opacity: 0.6; cursor: progress; }
.signup-gate-error {
  color: #fecaca;
  font-size: 0.8rem;
  margin-top: 0.4rem;
}
.btn-secondary {
  background: transparent;
  color: white;
  font-weight: 500;
  font-size: 0.85rem;
  padding: 0.5rem 0.9rem;
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.5);
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
}
.btn-secondary:hover { border-color: white; background: rgba(255, 255, 255, 0.08); }
</style>
